/**
 * Google Translate in the navbar — same widget as m3243, Europe-only list.
 * Hides Google’s banner (it would shove the map) and re-fires the combo
 * after IGNIS swaps innerHTML.
 */

const LANGUAGE_KEY = "ignisTranslateLanguage";
const AUTO_KEY = "ignisTranslateAutoHandled";

const WIDGET_SRC = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";

/** Google codes for languages spoken on the IGNIS map. English stays so you can switch back. No Russian. */
export const EUROPEAN_TRANSLATE_LANGS = [
  "bg",
  "ca",
  "hr",
  "cs",
  "da",
  "nl",
  "en",
  "et",
  "fi",
  "fr",
  "de",
  "el",
  "hu",
  "ga",
  "it",
  "lv",
  "lt",
  "mt",
  "no",
  "pl",
  "pt",
  "ro",
  "sk",
  "sl",
  "es",
  "sv",
  "sq",
  "eu",
  "gl",
  "is",
  "mk",
  "sr",
  "tr",
  "uk",
  "cy",
] as const;

declare global {
  interface Window {
    googleTranslateElementInit?: () => void;
    google?: {
      translate?: {
        TranslateElement: new (
          opts: {
            pageLanguage: string;
            includedLanguages?: string;
            autoDisplay: boolean;
            gaTrack: boolean;
          },
          id: string,
        ) => void;
      };
    };
  }
}

let installed = false;
let gtLoaded = false;
let gtLoading = false;
let selectBound = false;
let refreshTimer = 0;

function getSelect(): HTMLSelectElement | null {
  return document.querySelector("#google_translate_element select.goog-te-combo");
}

function googTransTarget(): string {
  const match = document.cookie.match(/(?:^|;\s*)googtrans=([^;]+)/);
  if (!match) return "";
  const parts = decodeURIComponent(match[1] ?? "").split("/");
  const target = parts[2] ?? "";
  return target && target !== "en" ? target : "";
}

function activeLanguage(): string {
  const fromSelect = getSelect()?.value ?? "";
  if (fromSelect && fromSelect !== "en") return fromSelect;
  return googTransTarget();
}

function setTranslateLanguage(select: HTMLSelectElement, language: string): void {
  if (!language) return;
  // Re-dispatch even when the value matches — that is how we re-walk new innerHTML.
  select.value = language;
  // Google’s combo still listens for the legacy HTMLEvents change, not a plain Event.
  const changeEvent = document.createEvent("HTMLEvents");
  changeEvent.initEvent("change", true, true);
  select.dispatchEvent(changeEvent);
}

function clearGoogleTranslateCookies(): void {
  // Google may set the cookie on the host or the dotted host.
  const expires = "Thu, 01 Jan 1970 00:00:00 GMT";
  const hostnames = [window.location.hostname, `.${window.location.hostname}`];
  document.cookie = `googtrans=; expires=${expires}; path=/`;
  for (const hostname of hostnames) {
    document.cookie = `googtrans=; expires=${expires}; path=/; domain=${hostname}`;
  }
}

function readStorage(key: string): string {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* Private mode — cookie-only persistence still works. */
  }
}

function removeStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* Ignore quota / privacy blocks. */
  }
}

function resetToEnglish(): void {
  // Reload so Google’s <font> wrappers are gone and the English DOM is clean.
  clearGoogleTranslateCookies();
  removeStorage(LANGUAGE_KEY);
  writeStorage(AUTO_KEY, "true");
  window.location.reload();
}

/** Pick the first browser locale that exists in Google’s combo (never English). */
export function matchPreferredLanguage(available: string[], locales: readonly string[]): string {
  const byLower = new Map(available.filter(Boolean).map((value) => [value.toLowerCase(), value]));
  for (const locale of locales) {
    const normalized = locale.replace(/_/g, "-").toLowerCase();
    const base = normalized.split("-")[0] ?? "";
    const matched = byLower.get(normalized) || byLower.get(base);
    if (matched && matched !== "en") return matched;
  }
  return "";
}

/** After panel / events / alerts HTML is replaced, ask Google to walk the new nodes. */
export function refreshTranslation(): void {
  if (!activeLanguage()) return;
  window.clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(() => {
    const select = getSelect();
    const language = select?.value || activeLanguage();
    if (select && language && language !== "en") setTranslateLanguage(select, language);
  }, 400);
}

export function installTranslate(): void {
  if (installed) return;
  const gtMount = document.getElementById("google_translate_element");
  if (!gtMount) return;
  installed = true;

  // Google’s script calls this by name once element.js finishes loading.
  window.googleTranslateElementInit = () => {
    const TranslateElement = window.google?.translate?.TranslateElement;
    if (!TranslateElement) return;
    // Default layout = native <select class="goog-te-combo">. SIMPLE is the
    // white Google button whose menu is an iframe we hide (clicks do nothing).
    new TranslateElement(
      {
        pageLanguage: "en",
        includedLanguages: EUROPEAN_TRANSLATE_LANGS.join(","),
        autoDisplay: false,
        gaTrack: false,
      },
      "google_translate_element",
    );
  };

  gtLoaded = !!document.querySelector('script[src*="translate.google.com/translate_a/element.js"]');

  function saveLanguage(language: string): void {
    if (!language || language === "en") removeStorage(LANGUAGE_KEY);
    else writeStorage(LANGUAGE_KEY, language);
    writeStorage(AUTO_KEY, "true");
  }

  function preferredFromSelect(select: HTMLSelectElement): string {
    const available = Array.from(select.options).map((option) => option.value);
    const locales = navigator.languages?.length ? navigator.languages : [navigator.language];
    return matchPreferredLanguage(available, locales);
  }

  function applyLanguage(select: HTMLSelectElement, language: string): void {
    if (!language || select.value === language) return;
    setTranslateLanguage(select, language);
  }

  function bindSelect(select: HTMLSelectElement): void {
    if (selectBound) return;
    select.setAttribute("aria-label", "Language");
    select.addEventListener("change", () => {
      const language = select.value || "";
      // Empty / English means “show the original page” — cookie-clear + reload.
      if (!language || language === "en") {
        if (googTransTarget() || readStorage(LANGUAGE_KEY)) resetToEnglish();
        else saveLanguage("");
        return;
      }
      saveLanguage(language);
    });
    selectBound = true;
  }

  function stripBranding(): void {
    // Google wraps the combo in a <span> and puts “Powered by” in the next one.
    // Hiding every gadget > span also hid the select.
    for (const span of [...gtMount.querySelectorAll(".goog-te-gadget > span")]) {
      if (!span.querySelector("select.goog-te-combo")) span.remove();
    }
  }

  function waitForSelect(attempt = 0): Promise<HTMLSelectElement | null> {
    const select = getSelect();
    if (select && select.options.length > 1) return Promise.resolve(select);
    if (attempt >= 40) return Promise.resolve(null);
    return new Promise((resolve) => {
      window.setTimeout(() => resolve(waitForSelect(attempt + 1)), 150);
    });
  }

  function loadGoogleTranslate(): void {
    if (gtLoaded || gtLoading) return;
    gtLoading = true;
    const script = document.createElement("script");
    script.src = WIDGET_SRC;
    script.onload = () => {
      gtLoaded = true;
      gtLoading = false;
    };
    script.onerror = () => {
      gtLoading = false;
    };
    document.head.appendChild(script);
  }

  async function applySavedOrPreferred(): Promise<void> {
    const select = await waitForSelect();
    if (!select) return;
    stripBranding();
    bindSelect(select);
    const saved = readStorage(LANGUAGE_KEY);
    if (saved && saved !== "en") {
      applyLanguage(select, saved);
      return;
    }
    if (readStorage(AUTO_KEY) === "true") return;
    const preferred = preferredFromSelect(select);
    if (preferred) {
      applyLanguage(select, preferred);
      saveLanguage(preferred);
      return;
    }
    writeStorage(AUTO_KEY, "true");
  }

  // Navbar select should be there on first paint — load immediately.
  if (!gtLoaded && !gtLoading) loadGoogleTranslate();
  void applySavedOrPreferred();
}

// privacy.html imports this file alone; the map also imports refreshTranslation.
if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => installTranslate());
  } else {
    installTranslate();
  }
}
